import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, ApiError } from '../lib/api.ts'
import { RecoveryCodes } from '../auth/RecoveryCodes.tsx'
import {
  WebauthnCancelled,
  registerSecurityKey,
  webauthnAvailable,
} from '../auth/webauthn.ts'

/**
 * Your own second factors and recovery codes (§6c).
 *
 * §6c says enrolment should encourage a backup factor and never require one —
 * so this screen exists to make adding the second one easy, and says why
 * without refusing to let anyone leave with a single factor. The one thing it
 * will not do is let you remove your last factor: there is no grace period, so
 * that is not turning 2FA off, it is locking yourself out.
 */

interface Factor {
  factor_id: string
  factor_type: string
  label: string | null
  created_at: string
  last_used_at: string | null
}

const TYPE_LABELS: Record<string, string> = {
  totp: 'Authenticator app',
  webauthn: 'Security key or passkey',
}

async function deleteFactor(id: string): Promise<void> {
  const response = await fetch(`/api/auth/factors/${id}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  })
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string } | null
    throw new ApiError(response.status, body?.message ?? 'Could not remove that factor.')
  }
}

export function Security() {
  const client = useQueryClient()
  const [codes, setCodes] = useState<string[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const { data, isPending } = useQuery({
    queryKey: ['factors'],
    queryFn: () =>
      apiGet<{ factors: Factor[]; recovery_codes_remaining: number }>('/auth/factors'),
  })

  const factors = data?.factors ?? []
  const remaining = data?.recovery_codes_remaining ?? 0

  async function addKey() {
    setBusy(true)
    setError(null)
    try {
      await registerSecurityKey('Security key')
      await client.invalidateQueries({ queryKey: ['factors'] })
    } catch (caught) {
      setError(
        caught instanceof WebauthnCancelled
          ? null
          : 'That key could not be registered. Try again.',
      )
    } finally {
      setBusy(false)
    }
  }

  const remove = useMutation({
    mutationFn: deleteFactor,
    onSuccess: async () => {
      setError(null)
      await client.invalidateQueries({ queryKey: ['factors'] })
    },
    onError: (caught) => setError(caught instanceof Error ? caught.message : 'That did not work.'),
  })

  const regenerate = useMutation({
    mutationFn: () => apiPost<{ codes: string[] }>('/auth/recovery-codes'),
    onSuccess: async (result) => {
      setError(null)
      setCodes(result.codes)
      await client.invalidateQueries({ queryKey: ['factors'] })
    },
    onError: () => setError('Could not generate new codes.'),
  })

  if (codes) {
    return (
      <div className="max-w-xl">
        <h1 className="text-xl font-semibold tracking-tight">New recovery codes</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Your previous codes no longer work.
        </p>
        <div className="mt-5">
          <RecoveryCodes codes={codes} onDone={() => setCodes(null)} doneLabel="Done" />
        </div>
      </div>
    )
  }

  const hasKey = factors.some((f) => f.factor_type === 'webauthn')

  return (
    <div className="max-w-2xl">
      <h1 className="text-xl font-semibold tracking-tight">Security</h1>
      <p className="mt-1 text-sm text-ink-muted">
        Any factor you have enrolled will sign you in. More than one is worth having — if you are
        the only administrator, nobody can reset yours for you.
      </p>

      {error ? (
        <p
          role="alert"
          className="mt-4 rounded-md border border-line bg-surface-sunken px-3 py-2 text-sm text-ink"
        >
          {error}
        </p>
      ) : null}

      <h2 className="mt-6 text-sm font-semibold tracking-wide text-ink-muted uppercase">
        Your factors
      </h2>
      <ul className="mt-2 divide-y divide-line border-y border-line">
        {isPending ? (
          <li className="py-3 text-sm text-ink-muted">Loading…</li>
        ) : (
          factors.map((f) => (
            <li key={f.factor_id} className="flex items-center justify-between gap-4 py-3">
              <div>
                <div className="text-sm font-medium">
                  {f.label ?? TYPE_LABELS[f.factor_type] ?? f.factor_type}
                </div>
                <div className="text-xs text-ink-muted">
                  {TYPE_LABELS[f.factor_type] ?? f.factor_type} · added{' '}
                  {f.created_at.slice(0, 10)}
                  {f.last_used_at ? ` · last used ${f.last_used_at.slice(0, 10)}` : ' · never used'}
                </div>
              </div>
              <button
                type="button"
                // Disabled rather than hidden on the last factor, with the
                // reason in the title: a missing button reads as a bug.
                disabled={factors.length <= 1 || remove.isPending}
                title={
                  factors.length <= 1
                    ? 'Enrol another factor before removing this one — an account with none cannot sign in.'
                    : undefined
                }
                onClick={() => remove.mutate(f.factor_id)}
                className="text-xs text-ink-muted hover:text-ink disabled:opacity-40"
              >
                Remove
              </button>
            </li>
          ))
        )}
      </ul>

      {!hasKey && webauthnAvailable() ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => void addKey()}
          className="mt-4 rounded-md border border-line px-3 py-1.5 text-sm font-semibold hover:border-brand-400 disabled:opacity-50"
        >
          {busy ? 'Waiting for your key…' : 'Add a security key or passkey'}
        </button>
      ) : null}

      <h2 className="mt-8 text-sm font-semibold tracking-wide text-ink-muted uppercase">
        Recovery codes
      </h2>
      <p className="mt-2 text-sm text-ink">
        {remaining === 0
          ? 'You have no recovery codes. If you lose every factor, an administrator has to reset you.'
          : `${remaining} unused code${remaining === 1 ? '' : 's'}.`}
      </p>
      <button
        type="button"
        disabled={regenerate.isPending}
        onClick={() => regenerate.mutate()}
        className="mt-3 rounded-md border border-line px-3 py-1.5 text-sm font-semibold hover:border-brand-400 disabled:opacity-50"
      >
        {remaining === 0 ? 'Generate recovery codes' : 'Generate a new set'}
      </button>
      {remaining > 0 ? (
        <p className="mt-2 text-xs text-ink-muted">
          Generating a new set cancels the codes you have now.
        </p>
      ) : null}

      <ChangePassword />
    </div>
  )
}

/**
 * Change your own password (POST /api/auth/password, migration 0082). The
 * current password is asked for again even though you are signed in, so an
 * unattended session cannot be used to take the account. Success signs you out
 * everywhere else.
 */
export function ChangePassword({ onChanged }: { onChanged?: () => void } = {}) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [done, setDone] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const change = useMutation({
    mutationFn: () =>
      apiPost<{ other_sessions_ended: number }>('/auth/password', {
        current_password: current,
        new_password: next,
      }),
    onSuccess: (r) => {
      setError(null)
      setCurrent('')
      setNext('')
      setConfirm('')
      setDone(
        r.other_sessions_ended > 0
          ? `Password changed. ${r.other_sessions_ended} other sign-in${r.other_sessions_ended === 1 ? ' was' : 's were'} ended.`
          : 'Password changed.',
      )
      onChanged?.()
    },
    onError: (caught) => {
      setDone(null)
      setError(
        caught instanceof ApiError && caught.status === 429
          ? 'Too many tries. Wait a minute and try again.'
          : caught instanceof Error
            ? caught.message
            : 'That did not work.',
      )
    },
  })

  const tooShort = next.length > 0 && next.length < 12
  const mismatch = confirm.length > 0 && confirm !== next
  const ready = current.length > 0 && next.length >= 12 && next === confirm && !change.isPending
  const field = 'w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-ink sm:w-80'

  return (
    <section aria-labelledby="password-heading">
      <h2 id="password-heading" className="mt-8 text-sm font-semibold tracking-wide text-ink-muted uppercase">
        Password
      </h2>
      <form
        className="mt-3 space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          if (ready) change.mutate()
        }}
      >
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium tracking-wide text-ink-muted uppercase">Current password</span>
          <input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} className={field} />
        </label>
        <div>
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium tracking-wide text-ink-muted uppercase">New password</span>
            <input type="password" autoComplete="new-password" aria-describedby="new-password-hint" value={next} onChange={(e) => setNext(e.target.value)} className={field} />
          </label>
          <p id="new-password-hint" className="mt-1 text-xs text-ink-muted">
            {tooShort ? `${12 - next.length} more character${12 - next.length === 1 ? '' : 's'} needed.` : 'At least 12 characters. A few unrelated words is easier to remember and harder to guess.'}
          </p>
        </div>
        <div>
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium tracking-wide text-ink-muted uppercase">New password again</span>
            <input type="password" autoComplete="new-password" aria-describedby="confirm-hint" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={field} />
          </label>
          {mismatch ? <p id="confirm-hint" className="mt-1 text-xs font-semibold">These don’t match.</p> : null}
        </div>
        {error ? <p role="alert" className="text-sm font-semibold">{error}</p> : null}
        {done ? <p role="status" className="text-sm">{done}</p> : null}
        <button
          type="submit"
          disabled={!ready}
          className="rounded-md border border-line px-3 py-1.5 text-sm font-semibold hover:border-brand-400 disabled:opacity-50"
        >
          {change.isPending ? 'Changing…' : 'Change password'}
        </button>
        <p className="text-xs text-ink-muted">Changing it signs you out on every other computer and browser. You stay signed in here.</p>
      </form>
    </section>
  )
}
